/* ==========================================================================
   TotalPantofi — FILTERS FIX & premium loading UX
   Problem: Dawn's facets.js calls element.focus() after filter update,
   which auto-scrolls the page to the sidebar (usually at top).
   Fix: lock + restore scroll position across the entire filter update cycle,
   override focus() to use {preventScroll:true} on facet elements, and keep
   the product grid visually stable with a smooth crossfade.
   ========================================================================== */
(function () {
  'use strict';

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  /* ------------------------------------------------------------------ */
  /* 1) Global focus override: when focus comes from a facet element,   */
  /*    always pass {preventScroll:true}.                                */
  /* ------------------------------------------------------------------ */
  var origFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function (options) {
    try {
      var inFacet = this.closest && (
        this.closest('.facets-container') ||
        this.closest('.facets-wrapper') ||
        this.closest('.facets__summary') ||
        this.closest('.mobile-facets') ||
        this.closest('facet-filters-form') ||
        this.closest('#FacetFiltersForm') ||
        this.closest('#FacetFiltersFormMobile')
      );
      if (inFacet) {
        return origFocus.call(this, { preventScroll: true });
      }
    } catch (e) {}
    return origFocus.apply(this, arguments);
  };

  /* ------------------------------------------------------------------ */
  /* 2) Scroll-lock during filter cycle                                   */
  /*    Save scrollY on facet input/change, and aggressively restore it  */
  /*    during the ~1500ms window where Dawn fetches and re-renders.     */
  /* ------------------------------------------------------------------ */
  var lockedY = null;
  var restoreTimers = [];

  function cancelRestore() {
    restoreTimers.forEach(function (t) { clearTimeout(t); });
    restoreTimers = [];
  }

  function startLock() {
    lockedY = window.scrollY || window.pageYOffset || 0;
    cancelRestore();
    // Aggressively restore on multiple frames over ~1.5s to overpower any
    // focus()/scrollIntoView that Dawn or a third-party script might fire.
    var stamps = [0, 30, 80, 150, 300, 500, 800, 1200, 1600];
    stamps.forEach(function (ms) {
      restoreTimers.push(setTimeout(function () {
        if (lockedY == null) return;
        var cur = window.scrollY || window.pageYOffset || 0;
        if (Math.abs(cur - lockedY) > 1) {
          window.scrollTo({ top: lockedY, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
        }
      }, ms));
    });
    // Release the lock after 2s — user interaction beyond that point is theirs
    restoreTimers.push(setTimeout(function () { lockedY = null; }, 2000));
  }

  // Also release the lock early if the user actually scrolls intentionally
  var userScrollReleased = false;
  function onUserScroll() {
    // Any wheel/touchmove from the user should end our lock immediately
    lockedY = null;
    cancelRestore();
  }
  window.addEventListener('wheel', onUserScroll, { passive: true });
  window.addEventListener('touchmove', onUserScroll, { passive: true });

  /* ------------------------------------------------------------------ */
  /* 3) Hook facet form events                                           */
  /* ------------------------------------------------------------------ */
  function isFacetElement(el) {
    return el && el.closest && (
      el.closest('facet-filters-form') ||
      el.closest('.facets-container') ||
      el.closest('.facets-wrapper') ||
      el.closest('.mobile-facets') ||
      el.closest('#FacetFiltersForm') ||
      el.closest('#FacetFiltersFormMobile') ||
      el.closest('#FacetFiltersPillsForm')
    );
  }

  document.addEventListener('input', function (e) {
    if (!isFacetElement(e.target)) return;
    startLock();
  }, true);
  document.addEventListener('change', function (e) {
    if (!isFacetElement(e.target)) return;
    startLock();
  }, true);
  document.addEventListener('click', function (e) {
    // "Remove filter" / "Clear all" buttons also trigger grid re-render
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('.active-facets__button, .mobile-facets__clear, facet-remove')) {
      startLock();
    }
  }, true);
  document.addEventListener('submit', function (e) {
    if (isFacetElement(e.target)) startLock();
  }, true);

  /* ------------------------------------------------------------------ */
  /* 4) Loading state: blur+dim grid, show spinner, re-enable after swap */
  /* ------------------------------------------------------------------ */
  var gridId = 'ProductGridContainer';
  function grid() { return document.getElementById(gridId); }

  var loadingSafetyTimer = null;
  function showLoading() {
    var g = grid();
    if (!g) return;
    g.classList.add('tp-grid-loading');
    // Safety: if for any reason hideLoading isn't called, auto-clear after 2.5s
    clearTimeout(loadingSafetyTimer);
    loadingSafetyTimer = setTimeout(function () {
      if (g) g.classList.remove('tp-grid-loading');
    }, 2500);
  }
  function hideLoading() {
    var g = grid();
    if (!g) return;
    g.classList.remove('tp-grid-loading');
    clearTimeout(loadingSafetyTimer);
  }

  document.addEventListener('input', function (e) {
    if (!isFacetElement(e.target)) return;
    showLoading();
  }, true);
  document.addEventListener('change', function (e) {
    if (!isFacetElement(e.target)) return;
    showLoading();
  }, true);

  // Observe grid swaps
  function observeGrid() {
    if (!('MutationObserver' in window)) return;
    var g = grid();
    if (!g) {
      setTimeout(observeGrid, 500);
      return;
    }
    var swapTimer;
    var mo = new MutationObserver(function () {
      clearTimeout(swapTimer);
      swapTimer = setTimeout(function () {
        hideLoading();
        // After swap, gently re-restore scroll if still locked
        if (lockedY != null) {
          var cur = window.scrollY || window.pageYOffset || 0;
          if (Math.abs(cur - lockedY) > 1) {
            window.scrollTo({ top: lockedY, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
          }
        }
        // Dispatch custom event so other modules can re-run
        document.dispatchEvent(new CustomEvent('tp:grid-updated'));
      }, 80);
    });
    mo.observe(g, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* 5) Prefetch the filter result on hover/first-click                  */
  /*    Makes the actual change feel instant — data is already warm.     */
  /* ------------------------------------------------------------------ */
  function prefetchOnHover() {
    document.addEventListener('pointerdown', function (e) {
      var label = e.target.closest && e.target.closest('label[for]');
      if (!label) return;
      if (!isFacetElement(label)) return;
      // Not strictly needed; Dawn's fetch caches — this is a no-op placeholder
      // that leaves an anchor for future enhancements.
    }, true);
  }

  /* ------------------------------------------------------------------ */
  /* 6) History navigation (Back/Forward): don't jump to top either      */
  /* ------------------------------------------------------------------ */
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  /* ------------------------------------------------------------------ */
  /* 7) Preserve clicked checkbox across re-render                       */
  /*    Dawn's renderCounts replaces the whole facets-wrap.outerHTML,    */
  /*    which destroys the input the user just clicked and causes a     */
  /*    visible check/uncheck flash. We override it to update only the  */
  /*    text counts beside each label, keeping all DOM inputs intact.   */
  /* ------------------------------------------------------------------ */
  function patchRenderCounts() {
    if (typeof FacetFiltersForm === 'undefined') {
      setTimeout(patchRenderCounts, 100);
      return;
    }
    FacetFiltersForm.renderCounts = function (source, target) {
      try {
        // Update the summary label (e.g. "Mărime (3)")
        var ts = target.querySelector('.facets__summary');
        var ss = source.querySelector('.facets__summary');
        if (ts && ss) {
          // Preserve open/closed state — only update label text if different
          var tLabel = ts.querySelector('.facets__summary-label, span, div');
          var sLabel = ss.querySelector('.facets__summary-label, span, div');
          if (tLabel && sLabel && tLabel.innerHTML !== sLabel.innerHTML) {
            tLabel.innerHTML = sLabel.innerHTML;
          }
        }

        // Update count "(N)" next to each existing checkbox without
        // replacing the input element itself.
        var sourceItems = source.querySelectorAll('.facets__item input[type="checkbox"]');
        sourceItems.forEach(function (sInput) {
          var id = sInput.id;
          if (!id) return;
          var tInput = target.querySelector('#' + CSS.escape(id));
          if (!tInput) return;
          var sLabel = sInput.closest('label') || sInput.parentElement;
          var tLabel = tInput.closest('label') || tInput.parentElement;
          if (!sLabel || !tLabel) return;

          // Sync disabled state
          if (sInput.disabled) {
            tInput.setAttribute('disabled', '');
            tLabel.classList.add('disabled');
          } else {
            tInput.removeAttribute('disabled');
            tLabel.classList.remove('disabled');
          }

          // Sync "active" class (for CSS highlight) but DO NOT touch the
          // checked state — user's click already set it correctly.
          if (sLabel.classList.contains('active')) {
            tLabel.classList.add('active');
          } else {
            tLabel.classList.remove('active');
          }

          // Copy count text: look for .facet-checkbox__text-label's sibling
          // text that contains "(N)"
          var sText = sLabel.querySelector('.facet-checkbox__text');
          var tText = tLabel.querySelector('.facet-checkbox__text');
          if (sText && tText && sText.innerHTML !== tText.innerHTML) {
            tText.innerHTML = sText.innerHTML;
          }
        });

        // Handle items that appear in source but not in target (new values)
        // and items in target not in source (removed values) — fallback to
        // original behavior if the set of ids changed.
        var sIds = Array.from(sourceItems).map(function (i) { return i.id; }).sort().join(',');
        var targetItems = target.querySelectorAll('.facets__item input[type="checkbox"]');
        var tIds = Array.from(targetItems).map(function (i) { return i.id; }).sort().join(',');
        if (sIds !== tIds) {
          // Ids changed — replace the wrap as before
          var tw = target.querySelector('.facets-wrap');
          var sw = source.querySelector('.facets-wrap');
          if (sw && tw) tw.outerHTML = sw.outerHTML;
        }
      } catch (err) {
        // On any failure, fall back to default behavior
        console.warn('[tp] renderCounts patch failed, falling back:', err);
        var tw2 = target.querySelector('.facets-wrap');
        var sw2 = source.querySelector('.facets-wrap');
        if (sw2 && tw2) tw2.outerHTML = sw2.outerHTML;
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* INIT                                                                 */
  /* ------------------------------------------------------------------ */
  function init() {
    patchRenderCounts();
    // Always clear any stray loading class on startup (prevents stuck spinner)
    var g = grid();
    if (g) g.classList.remove('tp-grid-loading');
    observeGrid();
    prefetchOnHover();
  }
  // Also clear on full page load (images done) as a final safety net
  window.addEventListener('load', function () {
    var g = grid();
    if (g) g.classList.remove('tp-grid-loading');
    clearTimeout(loadingSafetyTimer);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
